// Pure UI for the shell pane — the fourth tmux pane that wakes up the
// virtual filesystem hiding in filesystem.ts.
//
// Layout:
//   ┌─────────────────────────────────────────────────┐
//   │ [2] shell 🐚                          ~/career  │  ← header
//   ├─────────────────────────────────────────────────┤
//   │ <output buffer, oldest top>                     │  ← scroll area
//   │ thanh.nguyen@thanh-os:~/career$ ls              │     (echo)
//   │ about/  experience/  README.md  ...             │     (stdout)
//   │ thanh.nguyen@thanh-os:~/career$ _               │  ← live input
//   └─────────────────────────────────────────────────┘
//
// On mount, auto-runs `cat ~/README.md` exactly once so a fresh visitor
// sees the guide without having to type `help` first.
//
// The live prompt uses a *visible* inline <input> (transparent bg, native
// caret coloured green) so the browser handles IME (Vietnamese, CJK) and
// focus the way users expect. Clicking anywhere on the pane refocuses it.

import React, { useEffect, useRef } from 'react';
import { useShell } from './useShell';
import type { OutputEntry } from './useShell';
import type { ShellAction } from './shellCommands';
import styles from './Terminal.module.css';

const ACCENT = '#3ddc84';      // primary green — host + active border + caret
const PATH_COLOR = '#fbbf24';  // amber — cwd in prompt
const DOLLAR = '#9ca3af';      // gray-400 — $ separator
const ERROR_COLOR = '#fca5a5'; // red-300 — stderr lines
const GEMINI_COLOR = '#67e8f9'; // cyan-300 — Gemini fallback replies

interface ShellPaneProps {
  isActive: boolean;
  onActivate: () => void;
  onAction?: (action: ShellAction) => void;
}

// ---- Prompt fragments ----------------------------------------------------

interface PromptHeadProps {
  cwd: string;
}

/** "thanh.nguyen@thanh-os:~/career$ " — colored fragments inline. */
const PromptHead: React.FC<PromptHeadProps> = ({ cwd }) => (
  <>
    <span style={{ color: ACCENT }}>thanh.nguyen@thanh-os</span>
    <span style={{ color: DOLLAR }}>:</span>
    <span style={{ color: PATH_COLOR }}>{cwd}</span>
    <span style={{ color: DOLLAR }}>$&nbsp;</span>
  </>
);

// ---- Output row ----------------------------------------------------------

const OutputRow: React.FC<{ entry: OutputEntry }> = ({ entry }) => {
  if (entry.kind === 'echo') {
    return (
      <div className="whitespace-pre-wrap break-words">
        <PromptHead cwd={entry.cwd ?? '~'} />
        <span className="text-gray-100">{entry.text}</span>
      </div>
    );
  }
  if (entry.kind === 'stderr') {
    return (
      <div className="whitespace-pre-wrap break-words" style={{ color: ERROR_COLOR }}>
        {entry.text}
      </div>
    );
  }
  if (entry.kind === 'gemini') {
    // Cyan-tinted so visitors can tell at a glance "this is the AI riffing,
    // not a real command output". Show a prefix while the stream is still
    // empty so the line isn't blank during the first ~200ms.
    return (
      <div className="whitespace-pre-wrap break-words" style={{ color: GEMINI_COLOR }}>
        <span style={{ color: DOLLAR }}>[gemini] </span>
        {entry.text || (entry.streaming ? '…' : '')}
        {entry.streaming && (
          <span className={styles.caret} aria-label="streaming" />
        )}
      </div>
    );
  }
  return (
    <div className="whitespace-pre-wrap break-words text-gray-100">
      {entry.text}
    </div>
  );
};

// ---- Pane ----------------------------------------------------------------

const ShellPane: React.FC<ShellPaneProps> = ({ isActive, onActivate, onAction }) => {
  const {
    prettyCwd,
    input,
    setInput,
    output,
    exec,
    tab,
    recallPrev,
    recallNext,
    isStreaming,
  } = useShell({ onAction });

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const mountedRef = useRef<boolean>(false);

  // Auto-cat README.md exactly once when the pane first mounts.
  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;
    exec('cat ~/README.md');
  }, [exec]);

  // Autoscroll to bottom whenever new output lands.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [output, input]);

  // Refocus the input whenever this pane becomes active so typing "just works"
  // after a prefix-driven pane switch.
  useEffect(() => {
    if (isActive) inputRef.current?.focus();
  }, [isActive]);

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    // Let the tmux prefix (Ctrl+B) pass through to the global capturing
    // listener in useTmuxPaneSwitcher — that handler runs on `capture`
    // before this one, so no extra logic needed.
    if (e.key === 'Enter') {
      e.preventDefault();
      // Block during in-flight Gemini stream so the next command can't
      // interleave its echo + reply with the streaming tokens above.
      if (isStreaming) return;
      exec(input);
      setInput('');
      return;
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      tab();
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      recallPrev();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      recallNext();
      return;
    }
    if (e.key === 'l' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      exec('clear');
      setInput('');
      return;
    }
    if (e.key === 'c' && e.ctrlKey) {
      // Cancel the current input — push it as an echo so it stays in scroll
      // history, then drop the buffer.
      e.preventDefault();
      exec(input + '^C');
      setInput('');
      return;
    }
  }

  const paneStyle: React.CSSProperties = {
    fontFamily: "'JetBrains Mono', ui-monospace, monospace",
    fontSize: '13px',
    border: isActive ? '2px solid #3ddc84' : '2px solid #1f2937',
    boxShadow: isActive ? 'inset 0 0 20px rgba(61, 220, 132, 0.05)' : 'none',
    opacity: isActive ? 1 : 0.7,
    transition: 'opacity 120ms ease-out, border-color 120ms ease-out',
  };

  return (
    <div
      className="h-full w-full flex flex-col bg-surface-dark relative"
      style={paneStyle}
      onMouseDown={() => {
        // Activating via any click brings keyboard focus back to the input,
        // so users who click on a stale prompt line resume typing immediately.
        onActivate();
        inputRef.current?.focus();
      }}
    >
      {/* Header — no decorative name; the `shell` label + cwd already say it all. */}
      <div
        className="px-3 py-1 flex items-center gap-2 border-b border-gray-800 flex-shrink-0"
        style={{ color: ACCENT }}
      >
        <span className="text-[12px] text-gray-500">[2]</span>
        <span className="text-[12px] text-gray-500">shell</span>
        <span aria-hidden="true">🐚</span>
        <span className="ml-auto text-[11px] text-gray-500">{prettyCwd}</span>
      </div>

      {/* Output buffer + live input. The input sits inside the same scroll
          container so the prompt tracks with content instead of floating
          in its own row. */}
      <div
        ref={scrollRef}
        className={`flex-1 min-h-0 overflow-y-auto px-3 py-2 leading-relaxed ${styles.scrollArea}`}
      >
        {output.map((e) => (
          <OutputRow key={e.id} entry={e} />
        ))}

        {/* Live prompt — colored prefix + real <input> for native typing /
            IME / mobile keyboard support. Wrapped in a flex row so the input
            grows to fill the rest of the line. */}
        <div className="flex items-baseline whitespace-pre-wrap break-words">
          <span className="whitespace-pre">
            <PromptHead cwd={prettyCwd} />
          </span>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            onFocus={onActivate}
            autoCapitalize="off"
            autoCorrect="off"
            autoComplete="off"
            spellCheck={false}
            disabled={isStreaming}
            placeholder={isStreaming ? 'gemini is typing…' : undefined}
            aria-label="Career terminal input"
            className="flex-1 min-w-0 bg-transparent text-gray-100 border-0 outline-none p-0 m-0 disabled:opacity-50"
            style={{
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: '13px',
              caretColor: ACCENT,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ShellPane;
